<template>
  <view class="calendar-container">
    <view class="calendar-header">
      <view wx:if="{{title}}" class="calendar-title">{{ title }}</view>
      <slot />
      <week firstDay="{{firstDay}}" />
    </view>
    <scroll-view scroll-y class="calendar-body" wx:class="{{ {range:type === 'range'} }}" scroll-into-view="{{scrollview}}">
      <month
        wx:for="{{ months }}"
        wx:key="*this"
        firstDay="{{firstDay}}"
        id="month{{index}}"
        type="{{type}}"
        color="{{color}}"
        date="{{item}}"
        minDate="{{minDate}}"
        maxDate="{{maxDate}}"
        startText="{{startText}}"
        endText="{{endText}}"
        currentDate="{{currentDate}}"
        calendarHoliday="{{calendarHoliday}}"
        subTexts="{{subTexts}}"
        disableDate="{{disableDate}}"
        bind:click="onSelected"
      />
    </scroll-view>

    <view 
      class="calendar-footer" 
      wx:if="{{type === 'range'}}"
      > 
        <view wx:if="{{startTip || endTip}}" class="confirm-tip">
          <view wx:if="{{startTip}}" class="confirm-tip-item">{{startTip}}</view>
          <view wx:if="{{endTip}}" class="confirm-tip-item">{{endTip}}</view>
        </view>
        <Button
          class="confirm-button"
          disabled="{{forbidden}}" 
          bind:click="onConfirm"
        >确定</Button>
    </view>
  </view>
  </view>
</template>

<script>
  import mpx, { createComponent } from '@mpxjs/core'
  import {
    compare,
    getToday,
    calcDateNum,
    getFullDate,
    getDayByOffset,
    formatDate
  } from '@didi/ut-utils-lib'
  import { get } from '@didi/spring-base'
  /**
   * @description 获取日历节假日数据
   */
  const GET_HOLIDAYS = 'https://travel.xiaojukeji.com/train-toc/holidays'
  const getHolidayData = (params) => get(GET_HOLIDAYS, params)
  const getHolidayDataForFlights = (params) =>
    get('https://merak-flight.xiaojukeji.com/search-api/base/get_holiday')
  const getHolidayDataForTravel = (params) =>
    get('https://travel-alioth.xiaojukeji.com/ota/travel/store-api/base/get_holiday')

  createComponent({
    props: {
      bizType: {
        type: String,
        value: 'train' // flights
      },
      disabled: {
        type: Boolean,
        value: false
      },
      // 默认值 timestamp | timestamp[]
      value: {
        type: Array,
        optionalTypes: [Number, String]
      },
      // 日历类型 single
      type: {
        type: String,
        value: 'single'
      },
      // 标题
      title: {
        type: String,
        value: ''
      },
      // 选中底色
      color: {
        type: String,
        value: '#FF6400'
      },
      // 起始周
      firstDay: {
        type: Number,
        value: 0
      },
      // 可选择最小时间
      minDate: {
        type: Number,
        optionalTypes: [String],
        value: Date.now()
      },
      // 可选择最大时间
      maxDate: {
        type: Number,
        optionalTypes: [String],
        value: Date.now()
      },
      startText: {
        type: String,
        value: '去程'
      },
      endText: {
        type: String,
        value: '返程'
      },
      maxRange: {
        type: Number,
        value: 7
      },
      subTexts: {
        type: Array,
        value: []
      },
      disableDate: {
        type: Array,
        value: []
      }
    },

    data: {
      currentDate: [], // date|date[]
      calendarHoliday: [],
      rangeDateArr: []
    },

    watch: {
      value(val) {
        this.$nextTick(() => {
          this.currentDate = Array.isArray(val) ? val.slice() : [val]
        })
      }
    },

    computed: {
      scrollview() {
        const [minDate] = Array.isArray(this.value) ? this.value : [this.value]
        if (minDate) {
          const date = new Date(typeof minDate === 'string' ? minDate.replace(/-/g, '/') : minDate)
          date.setDate(1)
          date.setHours(0, 0, 0, 0)
          const index = this.months.indexOf(date.getTime())
          if (index > -1) {
            return `month${index}`
          }
        }
        return 'month0'
      },
      forbidden() {
        const { currentDate, type } = this
        if (currentDate == null) {
          return true
        }
        if (type === 'range') {
          return !currentDate[0] || !currentDate[1]
        }
        return !currentDate
      },
      startTip() {
        const _date = this.currentDate[0] ? formatDate(this.currentDate[0], 'MM月dd日') : ''
        return _date ? `${this.startText}：${_date}` : ''
      },
      endTip() {
        const _date = this.currentDate[1] ? formatDate(this.currentDate[1], 'MM月dd日') : ''
        return _date ? `${this.endText}：${_date}` : ''
      },
      months() {
        const times = []
        const { minDate, maxDate } = this
        const start = new Date(minDate)
        const end = new Date(maxDate)
        const endTime = end.getTime()
        end.setHours(0, 0, 0, 0)

        while (start.getTime() <= endTime) {
          start.setHours(0, 0, 0, 0)
          times.push(start.getTime())
          start.setDate(1)
          start.setMonth(start.getMonth() + 1)
        }
        return times
      }
    },

    methods: {
      toast(title) {
        mpx.showToast({
          title,
          icon: 'none'
        })
      },
      onSelected({ detail }) {
        const { type, currentDate, disabled } = this
        if (disabled) return

        const date = detail.date

        const getTime = (date) => (date instanceof Date ? date.getTime() : date)
        if (type === 'single') {
          this.select([date], true)
        } else if (type === 'range') {
          const [startDay, endDay] = currentDate
          if (startDay && !endDay) {
            const compareToStart = compare(date, startDay)

            if (compareToStart === 1) {
              this.select([startDay, date], true)
            } else if (compareToStart === -1) {
              this.select([date, null])
            } else {
              this.select([date, date], true)
            }
          } else {
            this.select([date, null])
          }
        }
      },
      select(date, complete) {
        if (complete && this.type === 'range' && !this.checkRange(date)) {
          this.emit([date[0], getDayByOffset(date[0], this.maxRange - 1)])
        } else {
          this.emit(date)
        }
        if (complete && this.type === 'single') {
          this.onConfirm()
        }
      },
      emit(date) {
        const getTime = (date) => (date instanceof Date ? date.getTime() : date)
        this.currentDate = Array.isArray(date) ? date.map(getTime) : [getTime(date)]
      },
      onConfirm() {
        if (!this.forbidden) {
          this.triggerEvent('confirm', this.currentDate)
        }
      },
      checkRange(date) {
        const { maxRange } = this
        const limitDate = calcDateNum(date[0], date[1])
        if (maxRange && limitDate > maxRange) {
          this.toast(`选择天数不能超过 ${maxRange} 天`)
          return false
        }
        return true
      },
      getTimestampsInRange(startTimestamp, endTimestamp) {
        const timestamps = []
        startTimestamp > endTimestamp &&
          ([startTimestamp, endTimestamp] = [endTimestamp, startTimestamp])
        const currentDate = new Date(startTimestamp)
        while (currentDate.getTime() <= endTimestamp) {
          timestamps.push(currentDate.getTime())
          currentDate.setDate(currentDate.getDate() + 1)
        }
        return timestamps
      },
      getInitialDate(defaultDate) {
        const now = getToday().getTime()

        if (!defaultDate || Array.isArray(defaultDate)) {
          defaultDate = now
        }
        return [this.limitDateRange(defaultDate)]
      },

      limitDateRange(date, minDate, maxDate) {
        minDate = minDate || this.minDate
        maxDate = maxDate || this.maxDate
        if (compare(date, minDate) === -1) {
          return minDate
        }
        if (compare(date, maxDate) === 1) {
          return maxDate
        }
        return date
      },
      /**
       * @description: 获取日历节假日数据
       */
      async getHoliday(params = {}) {
        if (this.calendarHoliday.length) return
        try {
          const getFn =
            this.bizType === 'flights'
              ? getHolidayDataForFlights
              : this.bizType === 'travel'
              ? getHolidayDataForTravel
              : getHolidayData
          const res = await getFn(params)
          const { config } = res?.data || {}
          this.calendarHoliday = config ? config : []
        } catch (error) {
          console.log(error)
        }
      }
    },

    created() {
      this.currentDate = this.value || this.getInitialDate()
      this.getHoliday()
    }
  })
</script>

<style lang='stylus' scoped>
  .calendar-container
    display flex
    flex-direction column
    background-color #fff
    padding-bottom 30px
    .calendar-header
      flex-shrink 0
      .calendar-title
        height 25px
        font-weight 500
        line-height 25px
        text-align center
        font-family PingFangSC-Medium
        font-size 18px
        color #000
        padding 16px 0
    .calendar-body
      height 413px
      &.range
        height 360px
    .calendar-footer
      box-sizing border-box
      padding 0 20px
      font-family PingFang SC
      .confirm-tip
        display flex
        align-items center
        margin 20px 0 6px
        font-size 13px
        font-weight 500
        color #333
        &-item:first-child
          margin-right 28px
      .confirm-button
        margin-top 10px
        height 50px
        line-height 50px
        border-radius 10px
        background #FF6400
        color #fff
        font-weight 600
        font-size 16px
</style>

<script name='json'>
  module.exports = {
    component: true,
    usingComponents: {
      week: './week',
      month: './month',
      Button: '../ts-button'
    }
  }
</script>